import type { UiNode, UiNodeInputAttributes } from '@ory/client';
import ActionButton from '@/components/Button/ActionButton';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { Spacer, VStack } from '@/components/Layout';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';

/** 表示順序: ボタンは入力フィールドの後に配置 */
export const ORDER = 80;
type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * Button inputノードコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるbutton inputフィールドをレンダリングします。
 * 主にWebAuthnなどの認証アクションをトリガーするために使用されます。
 *
 * `onclick`属性が存在する場合、JavaScriptコードを実行します。
 * シングルページアプリケーションでは、JSONソースからJavaScriptを実行するため、
 * この特別なケースでのみ`eval`を使用します。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#button-input-node
 */
export function ButtonInput({
  node,
  disabled
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();
  const attrs = node.attributes as UiNodeInputAttributes & {
    onclick?: string;
  };

  // button inputの場合はtype="button"である必要がある
  if (attrs.type !== 'button') {
    return null;
  }
  const label = node.meta?.label ? translateUiText(node.meta.label) : undefined;
  const errorMessages = node.messages?.filter(msg => msg.type === 'error').map(msg => translateUiText(msg));
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // onclick属性があれば実行（WebAuthnなどの場合）
    if (attrs.onclick) {
      try {
        // oxlint-disable-next-line eslint/no-eval -- Ory Kratos の onclick 属性実行に必要（WebAuthn 等）
        eval(attrs.onclick);
      } catch (error) {
        console.error('Failed to execute onclick:', error);
      }
    }
  };
  return <VStack className="w-full mt-4 gap-0">
      <ActionButton type="button" name={attrs.name} value={attrs.value as string} disabled={disabled ?? attrs.disabled} onClick={handleClick} variant="default" className="w-full">
        {label}
      </ActionButton>
      {errorMessages && errorMessages.length > 0 && <>
          <Spacer variant="xxs" />
          <ResponseMessage type="error" size="xs">
            {errorMessages[0]}
          </ResponseMessage>
        </>}
    </VStack>;
}